import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { FriedhofProvider, useFriedhof } from './contexts/FriedhofContext';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import ChangePassword from './pages/ChangePassword';
import NotFound from './pages/NotFound';
import Friedhoefe from './pages/Friedhoefe';
import Kunden from './pages/Kunden';
import KundeDetail from './pages/KundeDetail';
import Graeber from './pages/Graeber';
import GrabDetail from './pages/GrabDetail';
import Tiere from './pages/Tiere';
import TierDetail from './pages/TierDetail';
import VertragDetail from './pages/VertragDetail';
import Artikel from './pages/Artikel';
import Rechnungen from './pages/Rechnungen';
import RechnungDetail from './pages/RechnungDetail';
import RechnungEinstellungen from './pages/RechnungEinstellungen';
import LageplanKarte from './pages/LageplanKarte';
import Verwaltung from './pages/Verwaltung';
import Aktivitaet from './pages/Aktivitaet';
import './App.css';

const protectedPage = (element: React.ReactNode, requiredRole?: 'ADMIN') => (
  <ProtectedRoute requiredRole={requiredRole}>
    <Layout>{element}</Layout>
  </ProtectedRoute>
);

// /lageplan (no id) -> the currently selected Friedhof's map (or Dashboard if none chosen).
const LageplanRedirect: React.FC = () => {
  const { friedhofId } = useFriedhof();
  return <Navigate to={friedhofId ? `/lageplan/${friedhofId}` : '/dashboard'} replace />;
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
       <FriedhofProvider>
        <div className="App">
          {/* Unten mittig und etwas größer: oben rechts wurden Hinweise leicht übersehen (iPad). */}
          <Toaster
            position="bottom-center"
            containerStyle={{ bottom: 24 }}
            toastOptions={{
              duration: 5000,
              style: { fontSize: '15px', padding: '12px 16px', maxWidth: '560px', boxShadow: '0 8px 24px rgba(0,0,0,0.18)' },
            }}
          />
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route path="/dashboard" element={protectedPage(<Dashboard />)} />

            {/* Lageplan: georeferenced map per Friedhof (any employee); GeoJSON import is ADMIN */}
            <Route path="/lageplan" element={protectedPage(<LageplanRedirect />)} />
            <Route path="/lageplan/:friedhofId" element={protectedPage(<LageplanKarte />)} />

            <Route path="/friedhoefe" element={protectedPage(<Friedhoefe />)} />
            <Route path="/graeber" element={protectedPage(<Graeber />)} />
            <Route path="/graeber/:id" element={protectedPage(<GrabDetail />)} />
            <Route path="/tiere" element={protectedPage(<Tiere />)} />
            <Route path="/tiere/:id" element={protectedPage(<TierDetail />)} />
            <Route path="/kunden" element={protectedPage(<Kunden />)} />
            <Route path="/kunden/:id" element={protectedPage(<KundeDetail />)} />
            <Route path="/vertraege/:id" element={protectedPage(<VertragDetail />)} />
            <Route path="/artikel" element={protectedPage(<Artikel />)} />
            <Route path="/rechnungen" element={protectedPage(<Rechnungen />)} />
            <Route path="/rechnungen/:id" element={protectedPage(<RechnungDetail />)} />
            <Route path="/rechnungsdaten" element={protectedPage(<RechnungEinstellungen />, 'ADMIN')} />

            <Route path="/verwaltung" element={protectedPage(<Verwaltung />, 'ADMIN')} />
            <Route path="/aktivitaet" element={protectedPage(<Aktivitaet />, 'ADMIN')} />
            <Route path="/users" element={protectedPage(<Users />, 'ADMIN')} />
            <Route path="/change-password" element={protectedPage(<ChangePassword />)} />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
       </FriedhofProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
